import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../apis/slice/AuthSlice';
import { ExternalLoginApi } from '../apis/api/MembershipApi';
import { baseUrl } from '../apis/api/config';
import Wave from 'react-wavify';
import './Navbar.css';
import eliteLogo from '../elitebg1_new.png';

const Navbar = () => {
  const [expanded, setExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [ssoError, setSsoError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    dispatch(logout());
    // Clear all cookies
    document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'company_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'userID=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'access=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    navigate('/');
    setExpanded(false);
  };

  const handleProfileClick = async (e) => {
    e.preventDefault();
    
    // Get credentials from localStorage
    const credentialsStr = localStorage.getItem('memberCredentials');
    
    if (!credentialsStr) {
      alert('Please login again. Session expired.');
      navigate('/membership');
      return;
    }
    
    const credentials = JSON.parse(credentialsStr);
    
    let profileWindow = null;

    try {
      // Open new window first
      profileWindow = window.open('', '_blank');
      
      if (!profileWindow) {
        alert('Please allow popups for this site to open your profile.');
        return;
      }
      
      // Show loading in the new window
      profileWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Logging into CRM</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
              background: #ffffff;
              overflow: hidden;
            }
            
            .container {
              text-align: center;
              max-width: 400px;
            }
            
            .spinner {
              width: 48px;
              height: 48px;
              margin: 0 auto 32px;
              border: 3px solid rgba(212, 175, 55, 0.1);
              border-top-color: #D4AF37;
              border-radius: 50%;
              animation: spin 0.8s linear infinite;
            }
            
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
            
            h2 {
              font-size: 20px;
              font-weight: 600;
              color: #0B1F3A;
              margin-bottom: 8px;
              letter-spacing: -0.02em;
            }
            
            p {
              font-size: 14px;
              color: #64748b;
              font-weight: 400;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="spinner"></div>
            <h2>Logging into CRM</h2>
            <p>Please wait a moment...</p>
          </div>
        </body>
        </html>
      `);
      
      // Call SSO login via backend proxy to avoid CORS
      const ssoLoginResponse = await fetch(baseUrl + 'member/sso-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: credentials.email,
          password: credentials.password,
          loginFrom: 'Web'
        }),
      });
      
      const ssoLoginData = await ssoLoginResponse.json();
      
      if (ssoLoginData.status === 'success') {
        // Redirect to profile page
        const member = ssoLoginData.data.member;
        const access = ssoLoginData.data.membership_plan;
  
        // Build full_name safely
        const nameParts = [
          member?.first_name,
          member?.middle_name,
          member?.last_name,
        ].filter((part) => part && part !== "null" && part !== "undefined");
        const full_name = nameParts.join(" ");
  
        // Build user object with safe fallbacks
        const user = {
          fullname: full_name || "Guest User",
          company_name: member?.company_name ?? "No Company",
          member_logo: member?.member_logo ?? "/gray-avatar-icon-vector-illustration_276184-163.avif",
        };
        
        const query = new URLSearchParams({
          id: member.id,
          company_id: member.company_id,
          token: ssoLoginData.token,
          access: JSON.stringify(access),
          user: JSON.stringify(user)
        }).toString();

        const profileUrl = 'https://clubnet.app' + (ssoLoginData.redirect || `/nmmSetups/EFFN/Frontend/SSOHandler?${query}`);
        profileWindow.location.href = profileUrl;
      } else {
        // Close the new window and show error toast on current page
        profileWindow.close();
        setSsoError(ssoLoginData.message || 'SSO login failed. Please try again.');
        setTimeout(() => setSsoError(''), 4000);
      }
      
    } catch (error) {
      // Close the new window and show error toast on current page
      if (profileWindow && !profileWindow.closed) profileWindow.close();
      setSsoError('Failed to connect to profile service. Please try again.');
      setTimeout(() => setSsoError(''), 4000);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) setScrolled(isScrolled);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  return (
    <nav className={`navbar-modern ${scrolled ? 'scrolled' : ''}`}>
      <div className="container-fluid">
        <div className="navbar-container">
          {/* Logo */}
          <Link className="navbar-brand-modern" to="/">
            <div className="brand-logo">
              <div className="logo-icon">
               <img src={eliteLogo} alt="EFFN Logo" className="logo-image" />
              </div>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="navbar-menu">
            <div className="nav-links">
              <div className="nav-item">
                <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>Home</Link>
              </div>
              <div className="nav-item">
                <Link to="/about" className={`nav-link ${isActive('/about') ? 'active' : ''}`}>About</Link>
              </div>
              <div className="nav-item">
                <Link to="/benefits" className={`nav-link ${isActive('/benefits') ? 'active' : ''}`}>Benefits</Link>
              </div>
              <div className="nav-item">
                <Link to="/membership" className={`nav-link ${isActive('/membership') ? 'active' : ''}`}>Member Directory</Link>
              </div>
              <div className="nav-item">
                <Link to="/events" className={`nav-link ${isActive('/events') ? 'active' : ''}`}>
                  Events
                </Link>
              </div>
              <div className="nav-item">
                <Link to="/contact" className={`nav-link ${isActive('/contact') ? 'active' : ''}`}>Contact</Link>
              </div>
              {isAuthenticated ? (
                <>
                  <div className="nav-item">
                    <a href="#" onClick={handleProfileClick} className={`nav-link ${isActive('/member/profile') ? 'active' : ''}`}>
                      CRM Profile
                    </a>
                  </div>
                  <div className="nav-item">
                    <button onClick={handleLogout} className="nav-logout-btn">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                        <polyline points="16 17 21 12 16 7"/>
                        <line x1="21" y1="12" x2="9" y2="12"/>
                      </svg>
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <div className="nav-item">
                  <Link to="/join-us" className={`nav-link-btn-gold ${isActive('/join-us') ? 'active' : ''}`}>Become a Member</Link>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Toggle */}
          <button
            className="navbar-toggler-modern"
            onClick={() => setExpanded(!expanded)}
          >
            <span className={`toggler-line ${expanded ? 'open' : ''}`}></span>
            <span className={`toggler-line ${expanded ? 'open' : ''}`}></span>
            <span className={`toggler-line ${expanded ? 'open' : ''}`}></span>
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${expanded ? 'show' : ''}`}>
          <Link to="/" className="mobile-nav-link" onClick={() => setExpanded(false)}>Home</Link>
          <Link to="/about" className="mobile-nav-link" onClick={() => setExpanded(false)}>About</Link>
          <Link to="/benefits" className="mobile-nav-link" onClick={() => setExpanded(false)}>Benefits</Link>
          <Link to="/membership" className="mobile-nav-link" onClick={() => setExpanded(false)}>Member Directory</Link>
          <Link to="/events" className="mobile-nav-link" onClick={() => setExpanded(false)}>
            Events
          </Link>
          <Link to="/contact" className="mobile-nav-link" onClick={() => setExpanded(false)}>Contact</Link>
          {isAuthenticated ? (
            <>
              <a href="#" onClick={handleProfileClick} className={`mobile-nav-link ${isActive('/member/profile') ? 'active' : ''}`}>
                CRM Profile
              </a>
              <button onClick={handleLogout} className="mobile-nav-link mobile-logout-btn">
                <span>Logout</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </button>
            </>
          ) : (
            <Link to="/join-us" className={`mobile-nav-link mobile-nav-link-cta ${isActive('/join-us') ? 'active' : ''}`} onClick={() => setExpanded(false)}>Become a Member</Link>
          )}
        </div>
      </div>
      
      {/* SSO Error Toast - bottom right */}
      {ssoError && (
        <div style={{
          position: 'fixed',
          bottom: '28px',
          right: '24px',
          zIndex: 9999,
          animation: 'slideInRight 0.35s cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          <style>{`
            @keyframes slideInRight {
              from { opacity: 0; transform: translateX(40px) scale(0.95); }
              to   { opacity: 1; transform: translateX(0) scale(1); }
            }
          `}</style>
          <div style={{
            background: 'linear-gradient(135deg, #0B1F3A 0%, #162d52 100%)',
            borderRadius: '14px',
            padding: '0',
            maxWidth: '340px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(212,175,55,0.2)',
            overflow: 'hidden',
          }}>
            {/* Top accent bar */}
            <div style={{ height: '3px', background: 'linear-gradient(90deg, #D4AF37, #F5D77A, #D4AF37)' }} />
            
            <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
              {/* Icon */}
              <div style={{
                width: '38px', height: '38px', borderRadius: '10px',
                background: 'rgba(239,68,68,0.15)',
                border: '1px solid rgba(239,68,68,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>

              {/* Text */}
              <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: '14px', color: '#ffffff', letterSpacing: '-0.01em' }}>
                  Profile Login Failed
                </p>
                <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
                  Unable to open CRM profile. Please try again.
                </p>
              </div>

              {/* Close */}
              <button
                onClick={() => setSsoError('')}
                style={{
                  background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer',
                  color: 'rgba(255,255,255,0.5)', padding: '4px', borderRadius: '6px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'background 0.2s'
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Golden Wave Pattern - Using react-wavify */}
      <div className="wave-pattern">
        <Wave
          fill="url(#goldenWaveGradient)"
          paused={false}
          options={{
            height: 15,
            amplitude: 20,
            speed: 0.2,
            points: 6
          }}
        >
          <defs>
            <linearGradient id="goldenWaveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3a3a3a" />
              <stop offset="15%" stopColor="#b8860b" />
              <stop offset="35%" stopColor="#d4a017" />
              <stop offset="50%" stopColor="#f5e6a5" />
              <stop offset="65%" stopColor="#d4a017" />
              <stop offset="85%" stopColor="#b8860b" />
              <stop offset="100%" stopColor="#3a3a3a" />
            </linearGradient>
          </defs>
        </Wave>
      </div>
    </nav>
  );
};

export default Navbar;
